import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, MessageSquare } from "lucide-react";
import { MessageItem, type MessageView } from "@/components/MessageItem";
import type { Message } from "@/lib/types";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";

interface MessageListProps {
  messages: Message[];
  /** Last message ID the user has read. Newer messages get an unread band. */
  lastReadId?: string;
  onRead?: (id: string) => void;
  /** Called when the user scrolls near the top — wire up cursor pagination. */
  onLoadEarlier?: () => Promise<void> | void;
  /** True while the next page is being fetched. */
  loadingEarlier?: boolean;
  /** True when no further history exists. */
  reachedStart?: boolean;
  onRetry?: (localId: string) => void;
  onTaskify?: (id: string) => void;
  onReply?: (id: string) => void;
  onOpenThread?: (id: string) => void;
  onReact?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onPin?: (id: string) => void;
  onCopy?: (id: string) => void;
  onQuote?: (id: string) => void;
  onReport?: (id: string) => void;
  onBlock?: (id: string) => void;
  /** Signed-in member — report / block are not offered on their own messages. */
  currentUserId?: string;
}

const SCROLL_LOCK_PX = 80;
const GROUP_GAP_MS = 5 * 60_000; // 5 min

/** Bucket label per local date — re-rendered when the locale changes. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string, t: (k: string, opts?: object) => string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return t("message.today", { defaultValue: "今日" }) as string;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString())
    return t("message.yesterday", { defaultValue: "昨日" }) as string;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  });
}

/**
 * Threaded message log.
 *
 * Phase 1: virtualization-friendly DOM (each row is shallow); we keep the
 * tree flat so swapping in `react-virtual` is a one-line change.
 *
 * - Bottom-anchored on new messages, but only if the user was already at
 *   (or near) the bottom — preserves scroll position when reading history.
 * - "Jump to latest" pill when scrolled up away from the tail.
 * - Date separators per local day; first message of a same-author run
 *   shows the avatar/header, follow-ups are dense.
 * - Unread divider rendered above the first message newer than `lastReadId`.
 */
export function MessageList(props: MessageListProps): JSX.Element {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const wasAtBottom = useRef<boolean>(true);
  const [showJump, setShowJump] = useState(false);
  const lastLen = useRef<number>(0);
  const previousTop = useRef<string | undefined>();
  const previousHeight = useRef(0);

  // Memoised list with grouping + day separators.
  const rows = useMemo(() => buildRows(props.messages, props.lastReadId), [
    props.messages,
    props.lastReadId,
  ]);

  // Track if user is at the bottom (controls auto-stick + jump pill).
  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance < SCROLL_LOCK_PX;
    wasAtBottom.current = atBottom;
    previousHeight.current = el.scrollHeight;
    setShowJump(!atBottom);
    const last = props.messages[props.messages.length - 1];
    if (atBottom && !document.hidden && last?.status === "sent") props.onRead?.(last.id);
  }, [props.messages, props.onRead]);

  // Auto-scroll on new messages — only when user was already at the tail.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const grew = props.messages.length > lastLen.current;
    const first = props.messages[0]?.id;
    const prepended = grew && previousTop.current && first !== previousTop.current && props.messages.some(message => message.id === previousTop.current);
    lastLen.current = props.messages.length;
    if (prepended) el.scrollTop += el.scrollHeight - previousHeight.current;
    else if (grew && wasAtBottom.current) {
      el.scrollTop = el.scrollHeight;
      const last = props.messages[props.messages.length - 1];
      if (!document.hidden && last?.status === "sent") props.onRead?.(last.id);
    }
    previousTop.current = first;
    previousHeight.current = el.scrollHeight;
  }, [props.messages, props.onRead]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) onScroll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [onScroll]);

  // First mount: snap to bottom.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Infinite scroll — fire onLoadEarlier when the top sentinel becomes visible.
  useEffect(() => {
    if (!props.onLoadEarlier) return;
    const sentinel = sentinelRef.current;
    const root = scrollerRef.current;
    if (!sentinel || !root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !props.loadingEarlier && !props.reachedStart) {
            void props.onLoadEarlier?.();
          }
        }
      },
      { root, threshold: 0.01 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [props.onLoadEarlier, props.loadingEarlier, props.reachedStart]);

  if (props.messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-16 bg-surface-base">
        <div className="w-12 h-12 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center mb-3">
          <MessageSquare size={20} aria-hidden />
        </div>
        <p className="text-md font-medium text-fg-primary">
          {t("message.empty.title", { defaultValue: "まだ会話はありません。" }) as string}
        </p>
        <p className="text-sm mt-1 text-fg-muted max-w-sm">
          {t("message.empty.subtitle", {
            defaultValue: "最初のメッセージを送って、橋をかけましょう。",
          }) as string}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 relative bg-canvas">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        className="absolute inset-0 overflow-auto"
      >
        <div ref={sentinelRef} className="h-1" aria-hidden />
        {props.reachedStart ? (
          <p className="text-center text-xs text-fg-muted py-3">
            {t("message.start", { defaultValue: "ここがルームの始まりです。" }) as string}
          </p>
        ) : props.onLoadEarlier ? (
          <div className="flex justify-center py-2">
            <button
              type="button"
              onClick={() => void props.onLoadEarlier?.()}
              disabled={props.loadingEarlier}
              className="px-3 py-1 text-xs text-brand-700 hover:underline rounded focus-visible:shadow-focus"
            >
              {props.loadingEarlier
                ? (t("common.loading") as string)
                : (t("message.loadEarlier") as string)}
            </button>
          </div>
        ) : null}
        <ol className="py-2">
          {rows.map((row) => {
            if (row.kind === "day") {
              return (
                <li
                  key={`day-${row.dayKey}`}
                  className="flex items-center gap-3 px-4 py-3 select-none"
                >
                  <span className="flex-1 h-px bg-border-subtle" aria-hidden />
                  <span className="text-xs font-medium text-fg-muted bg-canvas px-2 rounded-full border border-border-subtle">
                    {dayLabel(row.iso, t as never)}
                  </span>
                  <span className="flex-1 h-px bg-border-subtle" aria-hidden />
                </li>
              );
            }
            if (row.kind === "unread") {
              return (
                <li
                  key="unread"
                  className="flex items-center gap-3 px-4 py-1 text-danger"
                >
                  <span className="flex-1 h-px bg-danger" aria-hidden />
                  <span className="text-[11px] font-medium uppercase tracking-wide">
                    {t("message.newMessages", { defaultValue: "新着メッセージ" }) as string}
                  </span>
                  <span className="flex-1 h-px bg-danger" aria-hidden />
                </li>
              );
            }
            return (
              <li key={row.message.id}>
                <MessageItem
                  message={row.message}
                  showHeader={row.showHeader}
                  unread={row.unread}
                  onRetry={props.onRetry}
                  onTaskify={props.onTaskify}
                  onReply={props.onReply}
                  onOpenThread={props.onOpenThread}
                  onReact={props.onReact}
                  onEdit={props.onEdit}
                  onDelete={props.onDelete}
                  onPin={props.onPin}
                  onCopy={props.onCopy}
                  onQuote={props.onQuote}
                  onReport={props.onReport}
                  onBlock={props.onBlock}
                  isOwn={!!props.currentUserId && row.message.authorId === props.currentUserId}
                />
              </li>
            );
          })}
        </ol>
      </div>

      {showJump ? (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            leadingIcon={<ArrowDown size={14} aria-hidden />}
            onClick={() => {
              const el = scrollerRef.current;
              if (el) el.scrollTop = el.scrollHeight;
            }}
            className={cn("shadow-md")}
          >
            {t("message.jumpToLatest", { defaultValue: "最新へ移動" }) as string}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Row construction                                                    */
/* ------------------------------------------------------------------ */

type Row =
  | { kind: "day"; dayKey: string; iso: string }
  | { kind: "unread" }
  | { kind: "msg"; message: MessageView; showHeader: boolean; unread: boolean };

function buildRows(messages: Message[], lastReadId?: string): Row[] {
  const out: Row[] = [];
  let prevDay: string | null = null;
  let prevAuthor: string | null = null;
  let prevTime = 0;
  let unreadInserted = !lastReadId;

  // Find the first index after `lastReadId`.
  const firstUnreadIdx = lastReadId
    ? messages.findIndex(
        (m, i) => i > 0 && messages[i - 1].id === lastReadId,
      )
    : -1;

  messages.forEach((m, i) => {
    const view = m as MessageView;
    const k = dayKey(m.createdAt);
    if (k !== prevDay) {
      out.push({ kind: "day", dayKey: k, iso: m.createdAt });
      prevDay = k;
      prevAuthor = null; // force header on day change
    }
    if (!unreadInserted && firstUnreadIdx === i) {
      out.push({ kind: "unread" });
      unreadInserted = true;
    }
    const t = new Date(m.createdAt).getTime();
    const isSystem = view.kind === "system";
    const showHeader =
      isSystem ||
      prevAuthor !== m.authorId ||
      t - prevTime > GROUP_GAP_MS;
    out.push({
      kind: "msg",
      message: view,
      showHeader,
      unread: !unreadInserted ? false : i >= firstUnreadIdx && firstUnreadIdx >= 0,
    });
    prevAuthor = isSystem ? null : m.authorId;
    prevTime = t;
  });
  return out;
}
